// The visible title of a phase.
//
// The scope summary is what a designer recognises a phase by — the `#ordinal` label says which
// phase, never which work — so it is the title, and the ordinal is only the fallback for a phase
// that has no summary at all.
//
// A card title has to fit one line at a glance, but nothing may be lost: the truncation is for
// the eye only, and the whole text stays reachable through `title` and `aria-label`. That is why
// this returns both, rather than returning a string and letting each caller decide.
export const SUB_TICKET_TITLE_MAX_LENGTH = 60;

const ELLIPSIS = '…';

export interface FormatSubTicketTitleParams {
    // Server-derived one-line summary of the phase scope
    scopeSummary?: string | null;
    // What to show when there is no summary — the immutable `Sub-task #N` label at every call site
    fallback: string;
    maxLength?: number;
}

export interface SubTicketTitle {
    // The whole title, for `title` and `aria-label`
    full: string;
    // The title as it is rendered, never longer than `maxLength`
    display: string;
    isTruncated: boolean;
}

export const formatSubTicketTitle = ({
    scopeSummary,
    fallback,
    maxLength = SUB_TICKET_TITLE_MAX_LENGTH,
}: FormatSubTicketTitleParams): SubTicketTitle => {
    const trimmed = (scopeSummary ?? '').trim();

    // No summary at all: the fallback is the whole title, so it was never truncated
    if (trimmed.length === 0) {
        return { full: fallback, display: fallback, isTruncated: false };
    }

    if (trimmed.length <= maxLength) {
        return { full: trimmed, display: trimmed, isTruncated: false };
    }

    // One character of the budget belongs to the ellipsis, so the rendered string is never
    // wider than the budget it was given
    const window = trimmed.slice(0, maxLength - ELLIPSIS.length);
    const boundary = window.lastIndexOf(' ');

    // Cutting mid-word turns a readable summary into a typo. A summary whose first word is
    // longer than the whole budget has no boundary to cut on, so it is cut hard rather than
    // allowed to overflow.
    const shown = (boundary > 0 ? window.slice(0, boundary) : window).trimEnd();

    return { full: trimmed, display: `${shown}${ELLIPSIS}`, isTruncated: true };
};
